import { baseCrudFactory } from "~/stores/base.crud.store.ts"
import { AdresseModel } from "~/model"
import { Api } from "~/api/Api"
import { useClientStore } from "~/stores"

function deg2rad(deg: number) {
    return deg * (Math.PI / 180)
}

function distanceInKm(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
): number {
    const theta = lon1 - lon2
    let dist =
        Math.sin(deg2rad(lat1)) * Math.sin(deg2rad(lat2)) +
        Math.cos(deg2rad(lat1)) *
            Math.cos(deg2rad(lat2)) *
            Math.cos(deg2rad(theta))
    dist = Math.acos(dist)
    dist = dist * (180 / Math.PI)
    return dist * 60 * 1.852
}

export const useAdresseStore = baseCrudFactory({
    endpoint: "adresse",
    primaryKey: "IDADRESSE",
    title: "adresse",
    model: AdresseModel,
    modules: {
        client: () => useClientStore(),
    },
    state: {
        radius: 0.5,
        // Filtres de la carte, par couleur Lola du projet
        filtreEnCours: true, // orange
        filtreRealises: true, // violet
        filtreProspects: true, // vert
        filtreAnnules: false, // rouge
        // Filtres par appartenance du client (symbole au centre du marqueur)
        filtreMesClients: true, // personne
        filtreMonAgence: true, // maison
        filtreAutresAgences: true, // vide
        // Filtre par date de RDV (AAAA-MM-JJ), applique par l'API
        filtreRdv: false,
        rdvDu: "",
        rdvAu: "",
        // Projets ayant du reste a faire (applique par l'API)
        filtreRaf: false,
        // Avec le filtre RDV, seules les adresses de la derniere reponse sont affichees
        // (le store garde aussi celles des recherches precedentes).
        arroundIds: null as number[] | null,
        arroundCenter: null as { latitude: number; longitude: number } | null,
    },
    actions: {
        async fetchAdresseArroundPoint(latitude: number, longitude: number) {
            if (
                typeof latitude !== "number" ||
                typeof longitude !== "number" ||
                isNaN(latitude) ||
                isNaN(longitude)
            ) {
                return []
            }
            this.arroundCenter = { latitude, longitude }
            const filtreRdv = this.filtreRdv && this.rdvDu && this.rdvAu
            const filtreServeur = filtreRdv || this.filtreRaf
            const response = await Api.client.clientArroundClient(
                latitude,
                longitude,
                this.radius,
                filtreRdv ? this.rdvDu : undefined,
                filtreRdv ? this.rdvAu : undefined,
                this.filtreRaf ? true : undefined,
            )
            this.setItems(response, false)
            this.arroundIds = filtreServeur
                ? response.map((adresse) => adresse.IDADRESSE)
                : null
            return response
        },
    },
    getters: {
        getAdresseArroundPoint() {
            const radius = this.radius
            const latitude = this.arroundCenter?.latitude
            const longitude = this.arroundCenter?.longitude

            if (
                latitude == null ||
                longitude == null ||
                radius == null ||
                isNaN(latitude) ||
                isNaN(longitude) ||
                isNaN(radius)
            ) {
                return []
            }

            const {
                filtreEnCours,
                filtreRealises,
                filtreProspects,
                filtreAnnules,
            } = this
            const appartenances = {
                mine: this.filtreMesClients,
                agence: this.filtreMonAgence,
                autre: this.filtreAutresAgences,
            }
            const idsRdv = this.arroundIds ? new Set(this.arroundIds) : null

            return this.items.filter((adresse) => {
                // Une adresse sans compteurs (cache d'avant les filtres) reste affichee.
                const aDesCompteurs = adresse.NB_CHANTIERS != null
                const visible =
                    !aDesCompteurs ||
                    (filtreEnCours && adresse.NB_ORANGE > 0) ||
                    (filtreRealises && adresse.NB_VIOLET > 0) ||
                    (filtreProspects && adresse.NB_VERT > 0) ||
                    (filtreAnnules && adresse.NB_ANNULE > 0)
                if (!visible) return false
                if (
                    typeof adresse.appartenance?.key === "string" &&
                    !appartenances[adresse.appartenance.key]
                )
                    return false
                if (idsRdv && !idsRdv.has(adresse.IDADRESSE)) return false

                if (
                    typeof adresse.LATITUDE !== "number" ||
                    typeof adresse.LONGITUDE !== "number"
                ) {
                    return false
                }

                const distance = distanceInKm(
                    latitude,
                    longitude,
                    adresse.LATITUDE,
                    adresse.LONGITUDE,
                )
                return distance <= radius
            })
        },
    },
})
